import type { SiteText } from "./types.js";

/** English site copy for a salon or barbershop owner who reads English. Edited by native readers, not a translation. */
export const en: SiteText = {
  nav: { pricing: "Pricing", terms: "Terms", refund: "Refunds", privacy: "Privacy", login: "Log in", home: "Home" },
  hero: {
    title: "Booking software with an AI assistant",
    text: "A calendar for salons and barbershops that work by appointment. The assistant answers clients' questions in Instagram Direct and books appointments into your calendar, using only the information you've entered.",
    cta: "See pricing",
    login: "Salon login",
    note: "No client prepayments, no bulk messages. Staff always have the final say.",
  },
  demo: {
    chatTitle: "Instagram Direct",
    messages: [
      { from: "client", text: "Hi! Any slots tomorrow after 17:00 for a fade?" },
      { from: "assistant", text: "Hi! We have 17:30 with Marko or 18:00 with Nikola. A fade takes 30 min and costs 1,500 RSD. Which one works for you?" },
      { from: "client", text: "17:30 with Marko." },
      { from: "assistant", text: "Fade with Marko tomorrow at 17:30, 1,500 RSD. Shall I book it?" },
      { from: "client", text: "Yes." },
      { from: "assistant", text: "Booked: tomorrow at 17:30, fade with Marko. See you then!" },
    ],
    bookingTitle: "Calendar",
    booking: { time: "17:30–18:00", client: "Luka", details: "Fade · Marko · booked by the assistant", status: "confirmed" },
  },
  how: {
    title: "How it works",
    steps: [
      {
        title: "Connect Instagram",
        text: "Connect your professional account through Meta's own login window. You never share your password with us, and we don't need access to your phone.",
      },
      {
        title: "Add your services and rules",
        text: "Upload a price list or a photo, or just type it in: prices, durations, staff, opening hours, cancellation rules. You review everything before the assistant goes live.",
      },
      {
        title: "The assistant replies and books",
        text: "It answers only from your salon's data, offers available times and books the one the client picks into your calendar. If it doesn't know the answer, it passes the conversation to a staff member.",
      },
    ],
  },
  features: {
    title: "What you get",
    items: [
      { title: "Calendar", text: "Services with prices and durations, staff, schedules and days off. Add an appointment manually in two taps." },
      {
        title: "AI replies",
        text: "Answers about prices, services, address, opening hours and rules, using only your salon's data. If it can't find the answer, the assistant says it will check with staff.",
      },
      { title: "Staff stay in charge", text: "All staff get notifications. Tap “Take over” and the assistant stays silent while you reply." },
      { title: "Booking page", text: "A link for your Instagram profile and a QR code, so clients can book without messaging." },
      { title: "Monthly summary", text: "One line a month: how many clients the assistant booked and how much those appointments were worth." },
      { title: "No client prepayments", text: "We never charge your clients and never send bulk messages." },
    ],
  },
  pricingTeaser: {
    title: "One price, no commissions",
    price: "€25",
    per: "per month",
    points: ["First month free: first charge after 30 days", "Unlimited conversations (a daily anti-spam limit applies)", "Cancel any time"],
    cta: "Full pricing",
    setup: "Setup is free: about 15 minutes, step by step.",
  },
  faq: {
    title: "Frequently asked questions",
    items: [
      {
        q: "Do clients know they're talking to an AI assistant?",
        a: "The assistant doesn't introduce itself as AI, but answers honestly if a client asks directly. You can also turn on a setting so it says so in its first message.",
      },
      {
        q: "What if the assistant doesn't know the answer?",
        a: "It tells the client it will check with staff and hands the conversation to you. Answer once, and the assistant will remember the answer next time.",
      },
      {
        q: "Can I take over a conversation?",
        a: "Yes, at any time. While a staff member is replying, the assistant stays silent. You can also pause it completely.",
      },
      {
        q: "What do I need to connect?",
        a: "The salon's professional Instagram account and a Meta business portfolio. You connect through Meta's own login window, so you never share passwords with us.",
      },
      {
        q: "How long does setup take?",
        a: "About 15 minutes with the setup wizard: services, staff, opening hours, rules. If you get stuck, write to us and we'll help.",
      },
      {
        q: "Do you charge my clients anything?",
        a: "No. We take no prepayments or deposits and send no bulk messages. You only pay for your salon's subscription.",
      },
      {
        q: "Can I cancel?",
        a: "Yes, any time from the billing page. You keep access until the end of the period you've paid for.",
      },
    ],
  },
  pricing: {
    title: "Pricing",
    subtitle: "One subscription for the whole salon: calendar, assistant, booking page and monthly summary.",
    plan: "Subscription",
    price: "€25",
    per: "per month",
    points: [
      "No commissions and no limits on conversations (a daily anti-spam limit applies)",
      "First month free: card required, first charge after 30 days",
      "Cancel any time, no annual contract",
    ],
    setupTitle: "Setup",
    self: { title: "Set it up yourself", price: "Free", text: "Use the setup wizard. It takes about 15 minutes. If you get stuck, we'll help." },
    vat: "Paddle calculates any applicable tax at checkout based on your country and shows it before you pay.",
    mor: "Payments are processed by Paddle.com, the Merchant of Record (the official seller) for all our orders.",
    start: "Get started",
  },
  footer: {
    rights: "All rights reserved.",
    tagline: "A calendar and an AI assistant for salons that work by appointment.",
    contact: "Contact",
    language: "Language",
    legal: "Legal",
  },
  draft: "Draft: this text has not yet been reviewed by a lawyer.",
  updated: "Last updated",
};
